import { usePlatformInsightsGetLoginHistory, useUserGetUserSiteData } from "@crowdcoursing/api/openapi";
import { keepPreviousData, skipToken } from "@tanstack/react-query";
import { DateTime } from "luxon";
import { useMemo, useState } from "react";

import { type DateRangeValue } from "~ui/atoms/Form/DateRange/PopoverDateRangePicker";
import { UserGrowthData } from "~ui/templates/AdminTemplate/DashboardStats/UserGrowthData";

const START_DATE_STORAGE_KEY = "userGrowthTab.startDate";

const getInitialTimeRange = (): DateRangeValue => {
  try {
    const stored = localStorage.getItem(START_DATE_STORAGE_KEY);
    if (stored) {
      const startDate = DateTime.fromISO(stored);
      if (startDate?.isValid) {
        return { endDate: DateTime.now(), startDate };
      }
    }
  } catch {
    // Fall through to default
  }
  return {
    endDate: DateTime.now(),
    startDate: DateTime.now().minus({ days: 30 }),
  };
};

const useUserGrowthDataProps = () => {
  const [timeRange, setTimeRange] = useState<DateRangeValue>(getInitialTimeRange);

  const handleTimeRangeChange = (newTimeRange: DateRangeValue) => {
    setTimeRange(newTimeRange);
    if (newTimeRange.startDate) {
      localStorage.setItem(START_DATE_STORAGE_KEY, newTimeRange.startDate.toISO() ?? "");
    }
  };

  const queryParams = useMemo(
    () => ({
      end: timeRange.endDate?.toISO() ?? DateTime.now().toISO(),
    }),
    [timeRange],
  );

  const loginHistoryQueryParams = useMemo(() => {
    if (!timeRange.startDate?.isValid || !timeRange.endDate?.isValid) {
      return undefined;
    }
    return {
      end: timeRange.endDate.plus({ days: 1 }).startOf("day").toISO() ?? undefined,
      start: timeRange.startDate.startOf("day").toISO() ?? undefined,
    };
  }, [timeRange.endDate, timeRange.startDate]);

  const { data, status } = useUserGetUserSiteData({ queryParams }, { placeholderData: keepPreviousData });

  const { data: loginHistory, status: loginHistoryStatus } = usePlatformInsightsGetLoginHistory(
    loginHistoryQueryParams ? { queryParams: loginHistoryQueryParams } : skipToken,
    { placeholderData: keepPreviousData },
  );

  return {
    data: data ?? null,
    loginHistory: loginHistory ?? null,
    loginHistoryStatus,
    onTimeRangeChange: handleTimeRangeChange,
    status,
    timeRange,
  };
};

export const UserGrowthTab = () => {
  const props = useUserGrowthDataProps();
  return <UserGrowthData {...props} />;
};
